import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, ContentCopy, PersonOutline, AdminPanelSettings } from "@mui/icons-material";
import { AdminUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import { useEffect, useRef, useState } from "react";
import {
  useUpdateAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useCreateAdminUserMutation,
  useDeleteAdminUserMutation,
  useGenerateAdminUserPasswordMutation,
} from "features/adminUsers/adminUsers";
import { useAppSelector } from "features/store";
import UserManagementChatBotTab from "./UserManagementChatBotTab";
import UserManagementFoldersTab from "./UserManagementFoldersTab";
import UserManagementIntegrationsTab from "./UserManagementIntegrationsTab";

interface UserManagementPanelProps {
  user: AdminUser | null;
  onClose: () => void;
  onUserUpdated: (user: AdminUser) => void;
  onUserDeleted?: () => void;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  isDestructive: boolean;
  onConfirm: () => void;
}

type TabId = "profile" | "folders" | "integrations" | "permissions" | "chatbot";

function rolesFromUser(u: AdminUser | null) {
  return {
    is_web_user: u?.profile.is_web_user ?? false,
    is_integration_user: u?.profile.is_integration_user ?? false,
    is_migration_user: u?.profile.is_migration_user ?? false,
    is_calibrate_admin: u?.profile.is_calibrate_admin ?? false,
    is_chat_bot_allowed: u?.profile.is_chat_bot_allowed ?? false,
  };
}

function permissionsFromUser(u: AdminUser | null) {
  return {
    is_bot_manager: u?.profile.is_bot_manager ?? false,
    is_integration_manager: u?.profile.is_integration_manager ?? false,
    is_sftp_manager: u?.profile.is_sftp_manager ?? false,
    is_user_and_folder_manager: u?.profile.is_user_and_folder_manager ?? false,
    is_delete_manager: u?.profile.is_delete_manager ?? false,
  };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return EMAIL_REGEX.test(trimmed);
}

export default function UserManagementPanel({
  user,
  onClose,
  onUserUpdated,
  onUserDeleted,
}: UserManagementPanelProps) {
  const isCreateMode = user === null;
  const currentUser = useAppSelector((state) => state.auth.user);
  const canEditProfile = Boolean(currentUser?.is_superuser || currentUser?.is_user_and_folder_manager);
  const canDeleteUsers = Boolean(currentUser?.is_superuser || currentUser?.is_delete_manager);
  const canEditIntegrations = Boolean(currentUser?.is_superuser || currentUser?.is_integration_manager);
  const canEditPermissions = Boolean(currentUser?.is_superuser);

  const PERMISSION_TIP = {
    createUser: "User & Folder Manager or Super Admin required to create users.",
    editProfile: "User & Folder Manager or Super Admin required to change profile and permissions.",
    folders: "Enable Web User in roles before managing folders.",
    permissions: "Super Admin only.",
    permissionsTab: "Enable Calibrate Admin in roles before managing permissions.",
    integrations: "Enable Integration User in roles before managing integrations.",
    chatbot: "Enable Chat Bot in roles before managing Chat Bot config.",
    deleteUser: "Delete Manager or Super Admin required to delete users. Super Admin cannot be deleted.",
  };

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState<TabId>("profile");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [initialPasswordForUser, setInitialPasswordForUser] = useState<{ userId: number; password: string; label: string } | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: "", severity: "success" });
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    message: "",
    isDestructive: false,
    onConfirm: () => {},
  });

  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [username, setUsername] = useState("");
  const [roles, setRoles] = useState(() => rolesFromUser(user));
  const [permissions, setPermissions] = useState(() => permissionsFromUser(user));
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(!user?.profile.is_pass_reset);

  useEffect(() => {
    setFirstName(user?.first_name ?? "");
    setEmail(user?.email ?? "");
    setUsername("");
    setRoles(rolesFromUser(user));
    setPermissions(permissionsFromUser(user));
    setRequiresPasswordChange(!user?.profile.is_pass_reset);
    setTab("profile");
    setDeleteConfirmText("");
    setInitialPasswordForUser((prev) => {
      if (user == null) return null;
      if (prev != null && prev.userId !== user.id) return null;
      return prev;
    });
  }, [user]);

  const isEmailInvalid = (email ?? "").trim() !== "" && !isValidEmail(email ?? "");
  const hasPermissionsChanges =
    user != null &&
    !(user.is_superuser && currentUser?.id === user.id) &&
    ((user.profile?.is_bot_manager ?? false) !== permissions.is_bot_manager ||
      (user.profile?.is_integration_manager ?? false) !== permissions.is_integration_manager ||
      (user.profile?.is_sftp_manager ?? false) !== permissions.is_sftp_manager ||
      (user.profile?.is_user_and_folder_manager ?? false) !== permissions.is_user_and_folder_manager ||
      (user.profile?.is_delete_manager ?? false) !== permissions.is_delete_manager);
  const hasProfileChanges =
    user != null &&
    !isEmailInvalid &&
    ((user.first_name ?? "").trim() !== firstName.trim() ||
      (user.email ?? "").trim() !== (email ?? "").trim() ||
      (user.profile?.is_web_user ?? false) !== roles.is_web_user ||
      (user.profile?.is_integration_user ?? false) !== roles.is_integration_user ||
      (user.profile?.is_migration_user ?? false) !== roles.is_migration_user ||
      (user.profile?.is_calibrate_admin ?? false) !== roles.is_calibrate_admin ||
      (user.profile?.is_chat_bot_allowed ?? false) !== roles.is_chat_bot_allowed ||
      (user.profile?.is_pass_reset === true) !== !requiresPasswordChange);

  const [updateUser, { isLoading: isSaving }] = useUpdateAdminUserMutation();
  const [createUser, { isLoading: isCreating }] = useCreateAdminUserMutation();
  const [resetPassword, { isLoading: isResetting }] = useResetAdminUserPasswordMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteAdminUserMutation();
  const [generatePassword, { isLoading: isGenerating }] = useGenerateAdminUserPasswordMutation();

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const handleTabChange = (_: unknown, value: TabId) => {
    if (value === "folders" && !isCreateMode && !user?.profile.is_web_user) {
      showToast("Enable Web User in roles before managing folders.", "error");
      return;
    }
    if (value === "integrations" && !isCreateMode && !user?.profile.is_integration_user) {
      showToast("Enable Integration User in roles before managing integrations.", "error");
      return;
    }
    if (value === "permissions" && !isCreateMode && !user?.profile.is_calibrate_admin) {
      showToast("Enable Calibrate Admin in roles before managing permissions.", "error");
      return;
    }
    if (value === "chatbot" && !isCreateMode && !user?.profile.is_chat_bot_allowed) {
      showToast("Enable Chat Bot in roles before managing Chat Bot config.", "error");
      return;
    }
    setTab(value);
  };

  const openConfirm = (opts: {
    title: string;
    message: string;
    isDestructive?: boolean;
    onConfirm: () => void;
  }) => {
    setConfirm({ open: true, isDestructive: false, ...opts });
  };

  const closeConfirm = () => {
    setConfirm((c) => ({ ...c, open: false }));
    setDeleteConfirmText("");
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      const updated = await updateUser({
        id: user.id,
        first_name: firstName,
        email,
        ...roles,
        is_pass_reset: !requiresPasswordChange,
      }).unwrap();
      onUserUpdated(updated);
      showToast("User updated successfully.", "success");
    } catch {
      showToast("Failed to update user.", "error");
    }
  };

  const handleSavePermissions = async () => {
    if (!user) return;
    try {
      const updated = await updateUser({ id: user.id, ...permissions }).unwrap();
      onUserUpdated(updated);
      showToast("Permissions updated.", "success");
    } catch {
      showToast("Failed to update permissions.", "error");
    }
  };

  const handleCreate = async () => {
    try {
      const created = await createUser({
        username,
        first_name: firstName,
        email,
        ...roles,
      }).unwrap();
      if (created.generated_password) {
        setInitialPasswordForUser({ userId: created.id, password: created.generated_password, label: "Initial Password" });
      }
      showToast("User created successfully.", "success");
      onUserUpdated(created);
    } catch (err: any) {
      const errors = err?.data?.errors;
      const msg = errors
        ? Object.values(errors).flat().join(" ")
        : "Failed to create user.";
      showToast(msg, "error");
    }
  };

  const handleResetPassword = () => {
    if (!user) return;
    openConfirm({
      title: "Reset Password",
      message: `Send a password reset email to ${user.email || user.username}?`,
      onConfirm: async () => {
        try {
          await resetPassword(user.id).unwrap();
          showToast("Password reset email sent.", "success");
        } catch {
          showToast("Failed to send reset email.", "error");
        }
      },
    });
  };

  const handleGeneratePassword = () => {
    if (!user) return;
    openConfirm({
      title: "Generate New Password",
      message: `Generate a new password for ${user.first_name || user.username}? The current password will be replaced immediately.`,
      onConfirm: async () => {
        try {
          const result = await generatePassword(user.id).unwrap();
          setInitialPasswordForUser({ userId: user.id, password: result.generated_password, label: "Generated Password" });
          showToast("Password generated.", "success");
        } catch {
          showToast("Failed to generate password.", "error");
        }
      },
    });
  };

  const handleDelete = () => {
    if (!user) return;
    openConfirm({
      title: "Delete User",
      message: `This will permanently delete ${user.first_name || user.username} and all their data. Type DELETE to confirm.`,
      isDestructive: true,
      onConfirm: async () => {
        try {
          await deleteUser(user.id).unwrap();
          showToast("User deleted successfully.", "success");
          onUserDeleted?.();
        } catch {
          showToast("Failed to delete user.", "error");
        }
      },
    });
  };

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username
    : "New User";

  return (
    <>
      <Box
        sx={{
          width: { xs: "100%", sm: 380, md: 420 },
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
          borderLeft: { xs: "none", sm: "1px solid" },
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: "rgba(31,93,107,0.10)",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: 14,
                color: "primary.main",
              }}
            >
              {isCreateMode ? (
                <PersonOutline fontSize="small" />
              ) : (
                fullName[0]?.toUpperCase()
              )}
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
                  {fullName}
                </Typography>
                {user?.is_superuser && (
                  <AdminPanelSettings sx={{ fontSize: 18, color: "primary.main" }} titleAccess="Super Admin" />
                )}
              </Box>
              {user && (
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    @{user.username}
                  </Typography>
                  <Chip
                    label={user.is_active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: user.is_active ? "rgba(46,125,50,0.12)" : "rgba(211,47,47,0.10)",
                      color: user.is_active ? "success.dark" : "error.dark",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            "& .MuiTabs-root": { overflow: "visible" },
            "& .MuiTabs-flexContainer": { flexWrap: "nowrap", width: "max-content", minWidth: "100%" },
            "& .MuiTabs-scroller": { overflow: "visible !important" },
            "& .MuiTabs-indicator": { transition: "none" },
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile={true}
            sx={{ minHeight: 44 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab value="profile" label="Profile" sx={{ minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1.5 }} />
          {!isCreateMode && (
            <Tab
              value="folders"
              label={
                user?.profile.is_web_user
                  ? "Folders"
                  : (
                    <Tooltip title={PERMISSION_TIP.folders} placement="top">
                      <span>Folders</span>
                    </Tooltip>
                  )
              }
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
          )}
          {!isCreateMode && (
            <Tab
              value="integrations"
              label={
                user?.profile.is_integration_user
                  ? (isNarrow ? "Integr." : "Integrations")
                  : (
                    <Tooltip title={PERMISSION_TIP.integrations} placement="top">
                      <span>{isNarrow ? "Integr." : "Integrations"}</span>
                    </Tooltip>
                  )
              }
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
          )}
          {!isCreateMode && (
            <Tab
              value="chatbot"
              label={
                user?.profile.is_chat_bot_allowed
                  ? (isNarrow ? "Chat" : "Chat Bot")
                  : (
                    <Tooltip title={PERMISSION_TIP.chatbot} placement="top">
                      <span>{isNarrow ? "Chat" : "Chat Bot"}</span>
                    </Tooltip>
                  )
              }
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
          )}
          {!isCreateMode && (
            <Tab
              value="permissions"
              label={
                user?.profile.is_calibrate_admin
                  ? (isNarrow ? "Perms" : "Permissions")
                  : (
                    <Tooltip title={PERMISSION_TIP.permissionsTab} placement="top">
                      <span>{isNarrow ? "Perms" : "Permissions"}</span>
                    </Tooltip>
                  )
              }
              sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
            />
          )}
        </Tabs>
        </Box>

        {tab === "folders" && user && (
          <UserManagementFoldersTab userId={user.id} canEdit={canEditProfile} onSaveSuccess={showToast} />
        )}

        {tab === "integrations" && user && (
          <UserManagementIntegrationsTab userId={user.id} canEdit={canEditIntegrations} onSuccess={showToast} />
        )}

        {tab === "chatbot" && user && (
          <UserManagementChatBotTab userId={user.id} canEdit={canEditProfile} onSuccess={showToast} />
        )}

        {tab === "permissions" && user && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1, ...(!canEditPermissions && { opacity: 0.7 }) }}>
                {user.is_superuser && currentUser?.id === user.id ? (
                  <Typography variant="body2" color="text.secondary">
                    You have full access to all permissions as a Super Admin.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing={0.6}
                      display="block"
                      mb={0.75}
                    >
                      Manager Access
                    </Typography>
                    <Box>
                      {(
                        [
                          { key: "is_bot_manager", label: "Bot Manager", disabled: true, comingSoon: true },
                          { key: "is_integration_manager", label: "Integration Manager", disabled: false, comingSoon: false },
                          { key: "is_sftp_manager", label: "SFTP Manager", disabled: true, comingSoon: true },
                          { key: "is_user_and_folder_manager", label: "User & Folder Manager", disabled: false, comingSoon: false },
                          { key: "is_delete_manager", label: "Delete Manager", disabled: false, comingSoon: false },
                        ] as const
                      ).map(({ key, label, disabled, comingSoon }) => {
                        const isDisabled = disabled || !canEditPermissions;
                        return (
                          <Box
                            key={key}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={0.4}
                            sx={!canEditPermissions ? { cursor: "default" } : undefined}
                          >
                            <Typography variant="body2" sx={disabled ? { color: "text.disabled" } : undefined}>
                              {label}
                              {comingSoon && (
                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                  (coming soon)
                                </Typography>
                              )}
                            </Typography>
                            <Tooltip
                              title={!canEditPermissions ? PERMISSION_TIP.permissions : ""}
                              placement="left"
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={permissions[key]}
                                  onChange={(e) =>
                                    !isDisabled && setPermissions((p) => ({ ...p, [key]: e.target.checked }))
                                  }
                                  disabled={isDisabled}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            </CustomScrollbars>
            {!(user.is_superuser && currentUser?.id === user.id) && (
              <Box px={2} pb={2} pt={1}>
                <Tooltip
                  title={
                    !canEditPermissions
                      ? PERMISSION_TIP.permissions
                      : !hasPermissionsChanges
                        ? "No changes to save"
                        : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSavePermissions}
                      disabled={!canEditPermissions || isSaving || !hasPermissionsChanges}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </>
        )}

        {tab === "profile" && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              {!isCreateMode && initialPasswordForUser?.userId === user?.id && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "rgba(46,125,50,0.08)",
                    border: "1px solid rgba(46,125,50,0.2)",
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" fontWeight={700} color="success.dark" textTransform="uppercase" letterSpacing={0.6}>
                      {initialPasswordForUser.label}
                    </Typography>
                    <IconButton size="small" onClick={() => setInitialPasswordForUser(null)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    value={initialPasswordForUser.password}
                    size="small"
                    fullWidth
                    inputProps={{ readOnly: true, style: { fontFamily: "monospace", fontWeight: 700 } }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(initialPasswordForUser.password)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
              {isCreateMode && (
                <Tooltip title={!canEditProfile ? PERMISSION_TIP.editProfile : ""} placement="top">
                  <span>
                    <TextField
                      label="Client Name"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      size="small"
                      fullWidth
                      required
                      disabled={!canEditProfile}
                      sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    />
                  </span>
                </Tooltip>
              )}
              <Tooltip title={!canEditProfile ? PERMISSION_TIP.editProfile : ""} placement="top">
                <span>
                  <TextField
                    label="Client Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    size="small"
                    fullWidth
                    required
                    disabled={!canEditProfile}
                    sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </span>
              </Tooltip>
              <Tooltip title={!canEditProfile ? PERMISSION_TIP.editProfile : ""} placement="top">
                <span>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="small"
                    fullWidth
                    disabled={!canEditProfile}
                    error={isEmailInvalid}
                    helperText={isEmailInvalid ? "Enter a valid email address" : undefined}
                    sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </span>
              </Tooltip>

              <Divider sx={{ mb: 1.5 }} />

              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.6}
                display="block"
                mb={0.75}
              >
                Roles
              </Typography>
              <Box>
                {(
                  [
                    { key: "is_migration_user", label: "Migration User" },
                    { key: "is_integration_user", label: "Integration User" },
                    { key: "is_web_user", label: "Web User" },
                    { key: "is_chat_bot_allowed", label: "Chat Bot" },
                    { key: "is_calibrate_admin", label: "Calibrate Admin" },
                  ] as const
                ).map(({ key, label }) => {
                  const isChatBot = key === "is_chat_bot_allowed";
                  const chatBotNeedsWebUser = isChatBot && !roles.is_web_user;
                  const isSelf =
                    !!currentUser && !!user && currentUser.id === user.id;
                  const isAdmin =
                    !!currentUser?.is_superuser || !!currentUser?.is_calibrate_admin;
                  const isSelfAdmin = isSelf && isAdmin;
                  const isCalibrateAdminKey = key === "is_calibrate_admin";
                  const isWebUserKey = key === "is_web_user";
                  const targetUserIsAdmin = !isCreateMode && !!(user?.profile?.is_calibrate_admin);
                  const webUserLockedByCalibrateAdmin =
                    isWebUserKey && targetUserIsAdmin && roles.is_web_user;
                  const cannotRemoveOwnRole =
                    isSelfAdmin &&
                    (isCalibrateAdminKey || isWebUserKey);
                  const disabled =
                    !canEditProfile ||
                    chatBotNeedsWebUser ||
                    cannotRemoveOwnRole ||
                    webUserLockedByCalibrateAdmin;
                  const tooltipTitle = !canEditProfile
                    ? PERMISSION_TIP.editProfile
                    : chatBotNeedsWebUser
                      ? "Enable Web User first"
                      : isCalibrateAdminKey && isSelfAdmin
                        ? "Admin cannot remove their own Calibrate Admin role."
                        : isWebUserKey && isSelfAdmin
                          ? "Admin cannot remove their own Web User role."
                          : webUserLockedByCalibrateAdmin
                            ? "Disable Calibrate Admin first to disable Web User."
                            : "";
                  return (
                    <Box
                      key={key}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      py={0.4}
                    >
                      <Typography variant="body2" color="text.primary" sx={disabled && isChatBot ? { color: "text.disabled" } : undefined}>
                        {label}
                      </Typography>
                      <Tooltip title={tooltipTitle} placement="left">
                        <span>
                          <Switch
                            size="small"
                            checked={roles[key]}
                            onChange={(e) => setRoles((r) => ({ ...r, [key]: e.target.checked }))}
                            disabled={disabled}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>

              {!isCreateMode && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    textTransform="uppercase"
                    letterSpacing={0.6}
                    display="block"
                    mb={0.75}
                  >
                    Security
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={0.4}
                    sx={{ minWidth: 0 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                      <Typography variant="body2" color="text.primary">
                        Require password change
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                        User will be forced to set a new password on next login
                      </Typography>
                    </Box>
                    <Tooltip title={!canEditProfile ? PERMISSION_TIP.editProfile : ""} placement="left">
                      <span>
                        <Switch
                          size="small"
                          checked={requiresPasswordChange}
                          onChange={(e) => setRequiresPasswordChange(e.target.checked)}
                          disabled={!canEditProfile}
                          sx={{ flexShrink: 0 }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </>
              )}
              </Box>
            </CustomScrollbars>
            <Box px={2} pb={2} pt={1} sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
              {isCreateMode ? (
                <Tooltip title={!canEditProfile ? PERMISSION_TIP.createUser : ""}>
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleCreate}
                      disabled={!canEditProfile || isCreating || !username.trim() || !firstName.trim() || isEmailInvalid}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      {isCreating ? "Creating..." : "Create User"}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <>
                  <Tooltip
                    title={
                      !canEditProfile
                        ? PERMISSION_TIP.editProfile
                        : !hasProfileChanges
                          ? "No changes to save"
                          : ""
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSave}
                        disabled={!canEditProfile || isSaving || !hasProfileChanges}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: "flex", gap: 1, "& > *": { flex: 1 } }}>
                    <Tooltip title={!canEditProfile ? PERMISSION_TIP.editProfile : ""}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleResetPassword}
                          disabled={!canEditProfile || isResetting || !user?.email}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                        >
                          Reset Password
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        user?.is_superuser
                          ? "Cannot generate password for Super Admin."
                          : !canEditProfile
                            ? PERMISSION_TIP.editProfile
                            : ""
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleGeneratePassword}
                          disabled={!canEditProfile || isGenerating || !!user?.is_superuser}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                        >
                          {isGenerating ? "Generating..." : "Generate Password"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  {!isCreateMode && user && (
                    <Tooltip
                      title={
                        !canDeleteUsers
                          ? PERMISSION_TIP.deleteUser
                          : user?.is_superuser
                            ? "Super Admin cannot be deleted."
                            : user?.id === currentUser?.id
                              ? "You cannot delete your own account."
                              : ""
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          color="error"
                          onClick={handleDelete}
                          disabled={!canDeleteUsers || isDeleting || !!user?.is_superuser || user?.id === currentUser?.id}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                        >
                          {isDeleting ? "Deleting..." : "Delete User"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
          {confirm.isDestructive && (
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder="Type DELETE to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm.isDestructive ? "error" : "primary"}
            disabled={confirm.isDestructive && deleteConfirmText !== "DELETE"}
            onClick={() => {
              closeConfirm();
              confirm.onConfirm();
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
